/**
 * @fileoverview Cart Manager
 * Main cart controller that coordinates API calls and events
 * 
 * 📡 EVENT SUMMARY:
 * 
 * EVENTS DISPATCHED:
 * - cart:loaded    → Emitted when initial cart is loaded
 * - cart:updated   → Emitted when cart state changes
 * - cart:added     → Emitted when item is added to cart
 * - cart:removed   → Emitted when item is removed from cart
 * - cart:cleared   → Emitted when cart is cleared
 * - operation:started   → Emitted when operation begins
 * - operation:completed → Emitted when operation succeeds
 * - operation:failed    → Emitted when operation fails
 * 
 * EVENTS LISTENED:
 * - submit (document) → Handles product form submissions
 * - click (document)  → Handles add to cart button clicks
 * - change (document) → Handles quantity input changes
 * 
 * EVENT DATA STRUCTURE:
 * - cart:loaded/updated: { cart: CartData, operationId: string }
 * - cart:added: { item: CartItem|Object, cart: CartData, operationId: string }
 * - cart:removed: { variantId: number, cart: CartData, operationId: string }
 * - cart:cleared: { cart: CartData, operationId: string }
 * - operation:*: { operationId: string, type: string, result?: any, error?: Error }
 */

import { CartAPI } from '../services/CartAPI.js';
import { EventEmitter } from './EventEmitter.js';
import { CART_EVENTS, DEFAULT_CONFIG, SELECTORS } from '../constants/index.js';
import { Performance, DOM, Debug, Format } from '../utils/index.js';
import { cartPerformance } from '../utils/Performance.js';

const CART_SESSION_KEY = 'shopify_cart';

/**
 * Cart Manager - Main cart controller
 * @class
 */
export class CartManager extends EventEmitter {
  /**
   * @param {CartConfig} [config] - Configuration options
   */
  constructor(config = {}) {
    super();
    
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.api = new CartAPI(this.config);
    this.cart = null;
    this.isLoading = false;
    this.loadingOperations = new Set();
    /** @type {Promise<unknown>} Serializes cart mutations so responses are applied in order (avoids removed items reappearing) */
    this._cartMutationLock = Promise.resolve();
    /** Timestamp when a user mutation (change qty, add, remove) last updated this.cart; used to avoid overwriting with stale loadInitialCart/refreshCartFromAPI response */
    this._lastMutationAt = 0;
    /** AbortController for in-flight loadInitialCart/refreshCartFromAPI getCart(); aborted when user changes qty or adds to cart */
    this._loadAbortController = null;
    this.eventCount = 0; // Track number of events for debugging
    this.pendingRequests = new Map(); // Request deduplication
    
    // Memoization cache
    this._cache = {
      itemCount: null,
      total: null,
      items: null,
      lastUpdate: null
    };
    
    // Performance tracking
    this.metrics = {
      apiCalls: 0,
      errors: 0,
      lastUpdate: null,
      events: new Map(), // Track event counts by type
      performance: {
        apiLatency: [],
        domUpdates: [],
        eventHandling: []
      }
    };

    // Initialize debounced quantity update with performance tracking
    this.debouncedQuantityUpdate = Performance.debounce((input) => {
      Performance.measure(() => {
        this.handleQuantityChange(input);
      }, 'quantityUpdate');
    }, this.config.debounceDelay);
    
    // Bind methods
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    
    // Override emit to add logging and performance tracking
    const originalEmit = this.emit.bind(this);
    this.emit = (event, data) => {
      const start = performance.now();
      
      this.eventCount++;
      this.metrics.events.set(event, (this.metrics.events.get(event) || 0) + 1);
      
      // Clear memoization cache and persist cart on cart updates
      if (event.startsWith('cart:')) {
        this._cache = {
          itemCount: null,
          total: null,
          items: null,
          lastUpdate: Date.now()
        };
        if (data?.cart && typeof data.cart === 'object') {
          this.persistCartToSession(data.cart);
        }
      }
      
      Debug.log('info', `[CartManager Event #${this.eventCount}] ${event}`, {
        event,
        data,
        metrics: {
          totalEvents: this.eventCount,
          eventCounts: Object.fromEntries(this.metrics.events),
          apiCalls: this.metrics.apiCalls,
          errors: this.metrics.errors,
          performance: this.metrics.performance
        }
      });
      
      const result = originalEmit(event, data);
      if (event === CART_EVENTS.ADDED) {
        this.openOcuCartIfEnabled();
      }
      
      // Track event handling performance
      const duration = performance.now() - start;
      this.metrics.performance.eventHandling.push({
        event,
        duration,
        timestamp: Date.now()
      });
      
      // Keep only last 100 measurements
      if (this.metrics.performance.eventHandling.length > 100) {
        this.metrics.performance.eventHandling.shift();
      }
      
      return result;
    };
    
    this.init();
  }

  /**
   * Initialize cart manager
   * @private
   */
  init() {
    Debug.log('info', 'Initializing CartManager');
    
    // Bind DOM events
    this.bindEvents();
    
    // Load initial cart state
    this.loadInitialCart();
    
    Debug.log('info', 'CartManager initialized');
  }

  /**
   * Bind DOM events
   * @private
   */
  bindEvents() {
    // Critical events first
    document.addEventListener('submit', this.handleFormSubmit, { passive: false, capture: true });
    document.addEventListener('click', this.handleClick, true);
    document.addEventListener('change', this.handleChange);
    // When user returns via back button, refetch cart so UI matches server (bfcache or full reload)
    this._pageShowCount = 0;
    window.addEventListener('pageshow', this.handlePageShow.bind(this));
  }

  /**
   * Open the OCU cart drawer after our add-to-cart flow succeeds.
   */
  openOcuCartIfEnabled() {
    if (!document.body.classList.contains('ocu-cart')) return;
    window.dispatchEvent(new CustomEvent('ocu:cart:open'));
  }

  /**
   * Handle pageshow – refetch cart when returning (bfcache or back) so UI matches server
   * @private
   * @param {PageTransitionEvent} event
   */
  handlePageShow(event) {
    this._pageShowCount = (this._pageShowCount || 0) + 1;
    const domCart = this.getCartFromDOM();
    const applied = this.applyCartFromSessionIfNewer(domCart);
    console.log('[CartManager] pageshow', {
      persisted: event.persisted,
      pageShowCount: this._pageShowCount,
      sessionApplied: applied
    });
    this.refreshCartFromAPI().catch(err => {
      console.error('[CartManager] refreshCartFromAPI failed after pageshow', err);
      Debug.log('error', '[CartManager] Failed to refresh cart after pageshow', err);
    });
  }

  /**
   * Fetch cart from API and emit cart:loaded + cart:updated so all UI (drawer, mini cart, counts) updates
   * @returns {Promise<Cart>} Cart data
   */
  async refreshCartFromAPI() {
    const operationId = 'refreshCartFromAPI';
    console.log('[CartManager] refreshCartFromAPI called', { operationId, loadingOperations: Array.from(this.loadingOperations) });
    if (this.loadingOperations.has(operationId)) {
      console.log('[CartManager] refreshCartFromAPI skipped (already in progress)');
      return null;
    }
    return this.executeOperation(operationId, async () => {
      const requestStartedAt = Date.now();
      this._loadAbortController = new AbortController();
      try {
        console.log('[CartManager] refreshCartFromAPI fetching /cart.js...');
        const cart = await this.api.getCart({ signal: this._loadAbortController.signal });
        if (this._lastMutationAt > requestStartedAt) {
          Debug.log('info', '[CartManager] Ignoring refresh cart response (user changed cart in the meantime)');
          return this.cart;
        }
        console.log('[CartManager] refreshCartFromAPI got cart', { item_count: cart?.item_count, items: cart?.items?.length });
        this.cart = cart;
        this.emit(CART_EVENTS.LOADED, { cart, operationId });
        this.emit(CART_EVENTS.UPDATED, {
          cart,
          operationId,
          itemCount: cart.item_count,
          items: cart.items,
          total: cart.total_price,
          subtotal: cart.items_subtotal_price
        });
        Debug.log('info', '[CartManager] Cart refreshed from API', { itemCount: cart.item_count });
        return cart;
      } catch (err) {
        if (err?.name === 'AbortError') {
          Debug.log('info', '[CartManager] Refresh cart request aborted (user changed cart)');
          return this.cart;
        }
        throw err;
      } finally {
        this._loadAbortController = null;
      }
    });
  }

  /**
   * Persist cart to sessionStorage (called on every cart change)
   * @param {Object} cart - Cart object to store
   */
  persistCartToSession(cart) {
    if (!cart || typeof cart !== 'object') return;
    try {
      sessionStorage.setItem(CART_SESSION_KEY, JSON.stringify(cart));
    } catch (e) {
      Debug.log('warn', '[CartManager] persistCartToSession failed', e);
    }
  }

  /**
   * Read cart from sessionStorage
   * @returns {Object|null} Parsed cart or null
   */
  getCartFromSession() {
    try {
      const raw = sessionStorage.getItem(CART_SESSION_KEY);
      if (!raw) return null;
      const cart = JSON.parse(raw);
      return cart && typeof cart === 'object' && Array.isArray(cart.items) ? cart : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Compare two carts for display purposes (item_count and item keys length)
   */
  _cartMatches(a, b) {
    if (!a && !b) return true;
    if (!a || !b) return false;
    const ai = a.item_count ?? a.items?.length ?? 0;
    const bi = b.item_count ?? b.items?.length ?? 0;
    if (ai !== bi) return false;
    const aKeys = (a.items || []).map(i => i.key).sort().join(',');
    const bKeys = (b.items || []).map(i => i.key).sort().join(',');
    return aKeys === bKeys;
  }

  /**
   * Apply session cart to state and UI for instant view if it differs from current/DOM
   * @param {Object|null} domOrCurrentCart - Cart from DOM (#CartState) or this.cart
   * @returns {boolean} Whether session was applied
   */
  applyCartFromSessionIfNewer(domOrCurrentCart) {
    const sessionCart = this.getCartFromSession();
    if (!sessionCart) return false;
    if (this._cartMatches(sessionCart, domOrCurrentCart || this.cart)) return false;
    this.cart = sessionCart;
    this.emit(CART_EVENTS.LOADED, { cart: sessionCart, operationId: 'session' });
    this.emit(CART_EVENTS.UPDATED, {
      cart: sessionCart,
      operationId: 'session',
      itemCount: sessionCart.item_count,
      items: sessionCart.items,
      total: sessionCart.total_price,
      subtotal: sessionCart.items_subtotal_price
    });
    Debug.log('info', '[CartManager] Applied cart from session (instant view)', { item_count: sessionCart.item_count });
    return true;
  }

  /**
   * Abort in-flight load/refresh cart.js request so user mutation result is not overwritten
   */
  abortLoadCartRequest() {
    if (this._loadAbortController) {
      this._loadAbortController.abort();
      this._loadAbortController = null;
      Debug.log('info', '[CartManager] Aborted in-flight cart.js request');
    }
  }

  /**
   * Read cart from DOM #CartState if present
   * @returns {Object|null}
   */
  getCartFromDOM() {
    const el = document.getElementById('CartState');
    if (!el || !el.textContent) return null;
    try {
      const cart = JSON.parse(el.textContent);
      return cart && typeof cart === 'object' ? cart : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Handle form submissions
   * 
   * 🔄 NEW STRATEGY:
   * We DO NOT manually extract properties/selling plan/quantity/etc.
   * We simply serialize the entire form via FormData and POST it directly
   * to /cart/add.js. This preserves any current/future fields without code changes.
   * 
   * 🧠 VariantManager note:
   * If a VariantManager is present, we only sync the variant id field `id`
   * to ensure the form reflects the *current* selected variant.
   * 
   * @private
   * @param {Event} event - Submit event
   */
  async handleFormSubmit(event) {
    const form = event.target;
    
    // Accept forms that either have data-product-form or contain data-submit-button
    const isProductForm = form.matches(SELECTORS.PRODUCT_FORM);
    const hasSubmitButton = form.querySelector('[data-submit-button]');
    
    if (!isProductForm && !hasSubmitButton) {
      Debug.log('debug', '[CartManager] Ignoring non-product form submit', {
        form: form.id || form.className,
        hasProductForm: isProductForm,
        hasSubmitButton: !!hasSubmitButton
      });
      return;
    }

    // Check if this is a buy now button (should redirect to checkout)
    // In that case we do NOT intercept; we let the browser naturally go to checkout.
    const buyNowButton = form.querySelector('[data-buy-now]');
    const returnToCheckout = form.querySelector('input[name="return_to"][value="/checkout"]');
    
    if (buyNowButton && returnToCheckout) {
      Debug.log('info', '[CartManager] Buy now button detected - allowing natural redirect to checkout');
      // Don't prevent default - let Shopify handle the redirect
      return;
    }

    // Mobile browsers can emit a second submit before the first cart request finishes.
    // Keep the lock on the form so duplicate CartManager instances are guarded too.
    if (form.hasAttribute('data-cart-submitting')) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      Debug.log('info', '[CartManager] Ignoring duplicate product form submit');
      return;
    }
    form.setAttribute('data-cart-submitting', 'true');

    Debug.log('info', '[CartManager] Handling product form submit', {
      form: form,
      formId: form.id
      // Note: we no longer log serialized fields here to avoid logging PII / bulky payloads
    });
    
    // We intercept the submit to perform an AJAX add-to-cart using the raw FormData
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Start performance tracking
    const performanceMarker = cartPerformance.createStartingMarker('add-to-cart:form-submit');

    // 🔸 Serialize the form AS-IS. This includes id, quantity, properties[], selling_plan, items[n][...], etc.
    const formData = new FormData(form);
    
    // 🧠 If we have a VariantManager, use its current variant ID to ensure accuracy.
    // We ONLY touch the `id` field when VariantManager is available and the form already has an 'id'.
    const useFormVariantId = form.hasAttribute('data-use-form-variant-id');
    if (window.variantManager?.getCurrentVariantId && formData.has('id') && !useFormVariantId) {
      console.log("GET VARIANT MANAGER CURRENT ID")
      const vmId = Number(window.variantManager.getCurrentVariantId());
      if (vmId) {
        formData.set('id', String(vmId));
        Debug.log('info', '[CartManager] Synced variant ID from VariantManager');
      }
    } else if (useFormVariantId) {
      Debug.log('info', '[CartManager] Using variant ID supplied by product form');
    }

    // Add loading state/feedback
    form.classList.add('is-loading');
    const submitButton = event.submitter instanceof HTMLElement
      ? event.submitter
      : form.querySelector('[type="submit"]');
    const buttonText = submitButton?.querySelector('[data-button-text]');
    const loadingSpinner = submitButton?.querySelector('[data-loading-spinner]');
    const originalText = buttonText ? buttonText.textContent : submitButton?.textContent;
    const checkmark = submitButton?.querySelector('[data-checkmark]') || form.querySelector('[data-checkmark]');

    if (document.querySelector('[data-mini-cart]') != undefined) {
      const sections = this.getSections();
      if (sections && sections.length) {
        formData.set('sections', sections.join(','));
        formData.set('sections_url', window.location.pathname);
      }
    }
    // site-header
    
    if (submitButton) {
      if (buttonText && loadingSpinner) {
        buttonText.style.opacity = '0';
        loadingSpinner.style.opacity = '1';
      } else {
        submitButton.textContent = 'Adding...';
      }
    }

    const operationId = `addItemsFromForm_${Date.now()}`;
    this.abortLoadCartRequest();

    try {
      /**
       * 🚀 POST the raw FormData to Shopify's /cart/add.js endpoint.
       * - Shopify accepts multipart/form-data (FormData) here.
       * - This supports both single item fields (id, quantity, properties[])
       *   and multi-add with items[n][id], items[n][quantity], etc.
       * - We set Accept: application/json so we can parse the response.
       */
      console.log("Submit Data")
      for (const [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      });

      // On failure, surface the response content to help debugging.
      if (!addRes.ok) {
        const text = await addRes.text().catch(() => '');
        throw new Error(`Add failed (${addRes.status}) ${text}`);
      }

      /**
       * Shopify typically returns the added line item object.
       * If the form used items[], Shopify may return an array-like payload.
       * We don't rely on the exact shape; we pass it along in the ADDED event for listeners.
       */
      const addedPayload = await addRes.json().catch(() => ({}));
      
      // Always fetch the fresh cart state so downstream UI is consistent.

      if (returnToCheckout && !buyNowButton) {
        window.location.href = '/checkout';
      }
      if (this.getSections().length > 0) {
        const cart = this.cart;
        this.emit(CART_EVENTS.ADDED, { item: addedPayload, sections: addedPayload.sections, operationId });
        this.emit(CART_EVENTS.UPDATED, {
          cart,
          operationId,
          itemCount: cart.item_count,
          items: cart.items,
          total: cart.total_price,
          subtotal: cart.items_subtotal_price,
          sections: addedPayload.sections
        });
      } else {
        const cart = await this.api.getCart();
        this.cart = cart;
        this._lastMutationAt = Date.now();

        // Emit events for any listeners (drawers, badges, toasts, etc.)
        this.emit(CART_EVENTS.ADDED, { item: addedPayload, cart, operationId });
        this.emit(CART_EVENTS.UPDATED, {
          cart,
          operationId,
          itemCount: cart.item_count,
          items: cart.items,
          total: cart.total_price,
          subtotal: cart.items_subtotal_price
        });
      }

      // Success feedback on the button
      if (submitButton) {
        if (buttonText && loadingSpinner) {
          loadingSpinner.style.opacity = '0';
          if (checkmark != undefined) {
            checkmark.classList.add('checkmark-active');
            setTimeout(() => {
              checkmark.classList.remove('checkmark-active');
              buttonText.style.opacity = '1';
            }, 2000);
          } else {
            buttonText.textContent = 'Added!';
            buttonText.style.opacity = '1';
            setTimeout(() => { buttonText.textContent = originalText; }, 2000);
          }
        } else {
          if (checkmark != undefined && buttonText) {
            checkmark.classList.add('checkmark-active');
            buttonText.style.opacity = '0';
            setTimeout(() => {
              buttonText.style.opacity = '1';
              checkmark.classList.remove('checkmark-active');
            }, 2000);
          } else if (submitButton) {
            submitButton.textContent = 'Added!';
            setTimeout(() => { submitButton.textContent = originalText; }, 2000);
          }
        }
      }
    } catch (error) {
      // Error feedback and event emission
      Debug.log('error', '[CartManager] Add to cart failed (serialized form send)', {
        error,
        formId: form.id,
        stackTrace: error.stack
      });
      if (submitButton) {
        if (buttonText && loadingSpinner) {
          buttonText.textContent = 'Error - Try Again';
          buttonText.style.opacity = '1';
          loadingSpinner.style.opacity = '0';
        } else if (submitButton) {
          submitButton.textContent = 'Error - Try Again';
        }
      }
      this.emit(CART_EVENTS.ERROR, { error, operationId });
    } finally {
      // Clear loading state and complete perf tracking
      form.classList.remove('is-loading');
      form.removeAttribute('data-cart-submitting');
      if (performanceMarker) {
        cartPerformance.measureFromMarker(performanceMarker);
      }
      Debug.log('info', '[CartManager] Form submission handling completed', {
        formId: form.id
      });
    }
  }

  /**
   * Handle click events
   * 
   * - Buy Now: let the browser perform the natural redirect to /checkout
   * - [data-submit-button]: trigger the native form submission so our submit handler runs
   * - Legacy buttons with [data-variant-id] outside a <form>: we build a tiny FormData and reuse the same send logic
   * 
   * @private
   * @param {Event} event - Click event
   */
  handleClick(event) {
    // Check for buy now button first (should redirect to checkout)
    const buyNowButton = event.target.closest('[data-buy-now]');
    if (buyNowButton) {
      const form = buyNowButton.closest('form');
      const returnToCheckout = form && form.querySelector('input[name="return_to"][value="/checkout"]');
      
      if (returnToCheckout) {
        Debug.log('info', '[CartManager] Buy now button clicked - allowing natural redirect to checkout');
        // Don't prevent default - let Shopify handle the redirect
        return;
      }
    }

    // ✅ data-submit-button: submit the parent form natively so our submit listener runs
    const submitTrigger = event.target.closest('[data-submit-button]');
    if (submitTrigger) {
      const explicitFormId = submitTrigger.getAttribute('form');
      const form = explicitFormId
        ? document.getElementById(explicitFormId)
        : submitTrigger.closest('form');
      if (!form) {
        Debug.log('error', '[CartManager] data-submit-button not inside a form');
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitTrigger);
      } else {
        // For older browsers
        form.submit();
      }
      return;
    }

    // Check for traditional add to cart buttons OUTSIDE a form
    const target = event.target.closest('[data-variant-id]');
    if (!target) return;
    
    if (target.matches(SELECTORS.ADD_TO_CART)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Start performance tracking
      const performanceMarker = cartPerformance.createStartingMarker('add-to-cart:button-click');

      const variantId = Number(target.dataset.variantId);
      const quantity = Number(target.dataset.quantity) || 1;
      
      if (!variantId) {
        Debug.log('error', 'No variant ID found on add to cart button');
        return;
      }

      // Add loading state
      target.classList.add('is-loading');
      const buttonText = target.querySelector('[data-button-text]');
      const loadingSpinner = target.querySelector('[data-loading-spinner]');
      const originalText = buttonText ? buttonText.textContent : target.textContent;
      
      if (buttonText && loadingSpinner) {
        buttonText.style.opacity = '0';
        loadingSpinner.style.opacity = '1';
      } else {
        target.textContent = 'Adding...';
      }

      // 🔸 Build a minimal FormData and POST it exactly like the form path
      (async () => {
        this.abortLoadCartRequest();
        const operationId = `addItemsFromButton_${Date.now()}`;
        try {
          const fd = new FormData();
          fd.set('id', String(variantId));
          fd.set('quantity', String(quantity));
          // add section rendering params when available
          if (document.querySelector('[data-mini-cart]') != undefined) {
            const sections = this.getSections();
            if (sections && sections.length) {
              fd.set('sections', sections.join(','));
              fd.set('sections_url', window.location.pathname);
            }
          }

          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json' },
            body: fd
          });
          if (!addRes.ok) {
            const text = await addRes.text().catch(() => '');
            throw new Error(`Add failed (${addRes.status}) ${text}`);
          }
          const addedPayload = await addRes.json().catch(() => ({}));

          // Refresh cart so UI/listeners are consistent
          const cart = await this.api.getCart();
          this.cart = cart;
          this._lastMutationAt = Date.now();

          this.emit(CART_EVENTS.ADDED, { item: addedPayload, cart, operationId });
          this.emit(CART_EVENTS.UPDATED, {
            cart,
            operationId,
            itemCount: cart.item_count,
            items: cart.items,
            total: cart.total_price,
            subtotal: cart.items_subtotal_price
          });

          if (buttonText && loadingSpinner) {
            buttonText.textContent = 'Added!';
            buttonText.style.opacity = '1';
            loadingSpinner.style.opacity = '0';
            setTimeout(() => { buttonText.textContent = originalText; }, 2000);
          } else {
            target.textContent = 'Added!';
            setTimeout(() => { target.textContent = originalText; }, 2000);
          }
        } catch (error) {
          Debug.log('error', 'Add to cart failed', error);
          if (buttonText && loadingSpinner) {
            buttonText.textContent = 'Error - Try Again';
            buttonText.style.opacity = '1';
            loadingSpinner.style.opacity = '0';
          } else {
            target.textContent = 'Error - Try Again';
          }
          this.emit(CART_EVENTS.ERROR, { error, operationId });
        } finally {
          target.classList.remove('is-loading');
          
          // Complete performance tracking
          if (performanceMarker) {
            cartPerformance.measureFromMarker(performanceMarker);
          }
        }
      })();
    }
  }

  /**
   * Handle change events (with debouncing)
   * @private
   * @param {Event} event - Change event
   */
  handleChange(event) {
    const input = event.target;
    if (!input.matches(SELECTORS.CART_QUANTITY)) return;

    // Start performance tracking
    const performanceMarker = cartPerformance.createStartingMarker('quantity-change:input');

    const variantId = Number(input.dataset.variantId);
    const quantity = Number(input.value) || 0;
    
    if (!variantId) {
      Debug.log('error', 'No variant ID found on quantity input');
      return;
    }

    // Store original value for rollback on error
    const originalValue = input.dataset.originalValue || input.defaultValue;
    
    this.changeItemQuantity(variantId, quantity)
      .then(() => {
        input.dataset.originalValue = quantity.toString();
      })
      .catch(error => {
        Debug.log('error', 'Quantity update failed', error);
        input.value = originalValue;
      })
      .finally(() => {
        // Complete performance tracking
        if (performanceMarker) {
          cartPerformance.measureFromMarker(performanceMarker);
        }
      });
  }

  /**
   * Load initial cart state from DOM or API
   * @private
   * @returns {Promise<Cart>} Cart data
   */
  async loadInitialCart() {
    const operationId = 'loadInitialCart';
    
    // Return early if this exact operation is already in progress
    if (this.loadingOperations.has(operationId)) {
      Debug.log('warn', '[CartManager] Initial cart load already in progress', {
        activeOperations: Array.from(this.loadingOperations)
      });
      return null;
    }

    return this.executeOperation(operationId, async () => {
      Debug.log('info', '[CartManager] Loading initial cart state');
      const domCart = this.getCartFromDOM();
      this.applyCartFromSessionIfNewer(domCart);
      const requestStartedAt = Date.now();
      this._loadAbortController = new AbortController();
      try {
        Debug.log('info', '[CartManager] Fetching cart from API (authoritative)');
        const cart = await this.api.getCart({ signal: this._loadAbortController.signal });
        if (this._lastMutationAt > requestStartedAt) {
          Debug.log('info', '[CartManager] Ignoring initial cart response (user changed cart in the meantime)');
          return this.cart;
        }
        this.cart = cart;
        this.emit(CART_EVENTS.LOADED, { cart, operationId });
        Debug.log('info', '[CartManager] Initial cart loaded', { itemCount: cart.item_count });
        return cart;
      } catch (err) {
        if (err?.name === 'AbortError') {
          Debug.log('info', '[CartManager] Initial cart request aborted (user changed cart)');
          return this.cart;
        }
        throw err;
      } finally {
        this._loadAbortController = null;
      }
    });
  }

  /**
   * Get current cart state
   * @returns {Promise<Cart>} Cart data
   */
  async getCart() {
    const operationId = `getCart_${Date.now()}`;
    
    // Check for pending request deduplication
    const pendingRequest = this.pendingRequests.get('getCart');
    if (pendingRequest) {
      Debug.log('info', '[CartManager] Deduplicating getCart request');
      return pendingRequest;
    }
    
    // If initial load is in progress, wait for it
    if (this.loadingOperations.has('loadInitialCart')) {
      Debug.log('info', '[CartManager] Waiting for initial cart load');
      return new Promise((resolve) => {
        this.once(CART_EVENTS.LOADED, ({ cart }) => {
          Debug.log('info', '[CartManager] Initial cart load completed');
          resolve(cart);
        });
      });
    }
    
    // Create and store the promise for deduplication
    const cartPromise = this.executeOperation(operationId, async () => {
      Debug.log('info', '[CartManager] Getting cart state');
      
      const cart = await this.api.getCart();
      this.cart = cart;
      
      Debug.log('info', '[CartManager] Cart state retrieved', {
        itemCount: cart.item_count,
        items: cart.items?.length || 0
      });
      
      return cart;
    });
    
    this.pendingRequests.set('getCart', cartPromise);
    
    // Clean up the pending request after completion
    cartPromise.finally(() => {
      this.pendingRequests.delete('getCart');
    });
    
    return cartPromise;
  }

  /**
   * Add item to cart
   * NOTE: Kept for backwards compatibility with any legacy callers.
   * The new submit path posts raw FormData and does not use this directly.
   * @param {number} variantId
   * @param {number} [quantity=1]
   * @param {Object} [properties={}]
   * @param {{selling_plan?: string|number, selling_plan_group_id?: string|number}} [plan={}]
   * @returns {Promise<{item: CartItem, cart: Cart}>}
   */
  async addToCart(variantId, quantity = 1, properties = {}, plan = {}) {
    const operationId = `addToCart_${variantId}_${Date.now()}`;
    
    // Return early if this exact operation is already in progress
    if (this.loadingOperations.has(operationId)) {
      Debug.log('warn', '[CartManager] Add to cart operation already in progress', {
        operationId,
        variantId,
        activeOperations: Array.from(this.loadingOperations)
      });
      return null;
    }

    return this.executeOperation(operationId, async () => {
      this.abortLoadCartRequest();
      Debug.log('info', '[CartManager] Adding item to cart', {
        operationId,
        variantId,
        quantity,
        properties,
        plan,
        activeOperations: Array.from(this.loadingOperations)
      });

      const result = await this.api.addToCart(variantId, quantity, properties, plan);
      
      // Update internal state first
      this.cart = result.cart;
      this._lastMutationAt = Date.now();
      
      Debug.log('info', '[CartManager] Item added successfully', {
        operationId,
        variantId
      });

      // Return result without emitting events - let executeOperation handle it
      return result;
    });
  }

  /**
   * Update cart items
   * @param {Object} updates - Updates object with variant IDs as keys and quantities as values
   * @returns {Promise<Cart>} Updated cart object
   */
  async updateCart(updates) {
    const operationId = `updateCart_${Date.now()}`;
    
    return this.executeOperation(operationId, async () => {
      this.abortLoadCartRequest();
      Debug.log('info', '[CartManager] Updating cart', { 
        updates,
        operationId
      });
      
      const cart = await this.api.updateCart(updates);
      
      // Update internal state
      this.cart = cart;
      this._lastMutationAt = Date.now();
      
      Debug.log('info', '[CartManager] Cart updated successfully', {
        operationId,
        newState: {
          itemCount: cart.item_count,
          items: cart.items?.length || 0
        }
      });
      
      return cart;
    });
  }

  /**
   * Change single item quantity
   * @param {number} variantId - Product variant ID
   * @param {number} quantity - New quantity
   * @returns {Promise<Cart>} Updated cart object
   */
  async changeItemQuantity(variantId, quantity) {
    const operationId = `changeQuantity_${variantId}_${Date.now()}`;
    if (this.loadingOperations.has(operationId)) {
      Debug.log('warn', '[CartManager] Quantity change operation already in progress', { operationId, variantId });
      return null;
    }

    const run = async () => {
      return this.executeOperation(operationId, async () => {
        this.abortLoadCartRequest();
        Debug.log('info', '[CartManager] Changing item quantity', { operationId, variantId, quantity });

        const currentCart = await this.getCart();
        const itemBeingChanged = currentCart.items.find(item => item.variant_id === variantId);

        if (quantity === 0 && itemBeingChanged) {
          const associatedUpsells = this.getAssociatedUpsells(itemBeingChanged.product_id, currentCart.items);
          if (associatedUpsells.length > 0) {
            Debug.log('info', '[CartManager] Found associated upsells to remove', {
              mainProductId: itemBeingChanged.product_id,
              upsellCount: associatedUpsells.length
            });
            for (const upsell of associatedUpsells) {
              await this.api.changeItemQuantity(upsell.variant_id, 0);
            }
            await this.getCart();
          }
        }

        const cart = await this.api.changeItemQuantity(variantId, quantity);
        this.cart = cart;
        this._lastMutationAt = Date.now();
        this.metrics.lastUpdate = Date.now();

        Debug.log('info', '[CartManager] Item quantity changed', {
          operationId, variantId, quantity,
          newState: { itemCount: cart.item_count, items: cart.items?.length || 0 }
        });

        this.emit('cart:updated', { cart, operation: 'changeItemQuantity', variantId, quantity });
        if (cart.item_count === 0) {
          this.emit('cart:emptied', { cart });
        }
        return cart;
      });
    };

    const prevLock = this._cartMutationLock;
    this._cartMutationLock = prevLock
      .then(() => run(), () => run())
      .then(r => r, err => { throw err; });
    return this._cartMutationLock;
  }

  /**
   * Remove item from cart
   * @param {number} variantId - Product variant ID
   * @returns {Promise<Cart>} Updated cart object
   */
  async removeFromCart(variantId) {
    return this.changeItemQuantity(variantId, 0);
  }

  /**
   * Check if an item is an upsell
   * @param {Object} item - Cart item
   * @returns {boolean} True if item is an upsell
   */
  isUpsellItem(item) {
    return item.properties && 
           item.properties._upsell === 'true';
  }

  /**
   * Get upsells associated with a main product
   * @param {number} mainProductId - Main product ID
   * @param {Array} cartItems - Cart items array
   * @returns {Array} Array of associated upsell items
   */
  getAssociatedUpsells(mainProductId, cartItems) {
    return cartItems.filter(item => {
      return this.isUpsellItem(item) && 
             item.properties._main_product_id === mainProductId.toString();
    });
  }

  /**
   * Clear entire cart
   * @returns {Promise<Cart>} Empty cart object
   */
  async clearCart() {
    const operationId = `clearCart_${Date.now()}`;
    
    return this.executeOperation(operationId, async () => {
      Debug.log('info', '[CartManager] Clearing cart', {
        operationId
      });
      
      const cart = await this.api.clearCart();
      
      // Update internal state
      this.cart = cart;
      this.metrics.lastUpdate = Date.now();
      
      Debug.log('info', '[CartManager] Cart cleared', {
        operationId,
        newState: {
          itemCount: cart.item_count,
          items: cart.items?.length || 0
        }
      });
      
      return cart;
    });
  }

  /**
   * Execute cart operation with loading state management
   * @private
   * @param {string} operationId - Unique operation identifier
   * @param {Function} operation - Operation to execute
   * @returns {Promise<any>} Operation result
   */
  async executeOperation(operationId, operation) {
    Debug.log('info', `[CartManager] Starting operation: ${operationId}`, {
      operationId,
      activeOperations: Array.from(this.loadingOperations),
      isLoading: this.isLoading
    });

    // Prevent duplicate operations
    if (this.loadingOperations.has(operationId)) {
      Debug.log('warn', `[CartManager] Operation ${operationId} already in progress`, {
        activeOperations: Array.from(this.loadingOperations)
      });
      return;
    }

    this.loadingOperations.add(operationId);
    this.isLoading = this.loadingOperations.size > 0;

    try {
      this.metrics.apiCalls++;
      const result = await operation();
      if (operationId === 'refreshCartFromAPI') {
        console.log('[CartManager] executeOperation refreshCartFromAPI completed', { hasResult: !!result, item_count: result?.item_count });
      }
      Debug.log('info', `[CartManager] Operation completed: ${operationId}`, {
        operationId,
        result,
        metrics: this.metrics
      });
      
      // Emit events only once with operation ID
      if (result?.cart) {
        // Update internal state first
        this.cart = result.cart;
        
        // For add to cart operations
        if (operationId.startsWith('addToCart_') && result.item) {
          this.emit(CART_EVENTS.ADDED, { 
            item: result.item,
            cart: this.cart,
            operationId
          });
        }
        
        // For all cart operations, ensure we emit the updated cart state
        this.emit(CART_EVENTS.UPDATED, { 
          cart: this.cart,
          operationId,
          itemCount: this.cart.item_count,
          items: this.cart.items,
          total: this.cart.total_price,
          subtotal: this.cart.items_subtotal_price
        });
      }
      
      return result;
    } catch (error) {
      this.metrics.errors++;
      Debug.log('error', `[CartManager] Operation failed: ${operationId}`, {
        operationId,
        error,
        stackTrace: error.stack
      });
      
      // Emit error event with operation ID
      this.emit(CART_EVENTS.ERROR, { 
        error,
        operationId
      });
      
      throw error;
    } finally {
      this.loadingOperations.delete(operationId);
      this.isLoading = this.loadingOperations.size > 0;
    }
  }

  /**
   * Get current cart item count with memoization
   * @returns {number} Item count
   */
  getItemCount() {
    if (this._cache.itemCount !== null && Date.now() - this._cache.lastUpdate < 1000) {
      return this._cache.itemCount;
    }
    this._cache.itemCount = this.cart?.item_count || 0;
    return this._cache.itemCount;
  }

  /**
   * Get current cart total with memoization
   * @returns {number} Cart total in cents
   */
  getTotal() {
    if (this._cache.total !== null && Date.now() - this._cache.lastUpdate < 1000) {
      return this._cache.total;
    }
    this._cache.total = this.cart?.total_price || 0;
    return this._cache.total;
  }

  /**
   * Get formatted cart total
   * @returns {string} Formatted total price
   */
  getFormattedTotal() {
    const total = this.getTotal();
    return Format.money(total, this.config.moneyFormat);
  }

  /**
   * Get formatted cart subtotal
   * @returns {string} Formatted subtotal price
   */
  getFormattedSubtotal() {
    const subtotal = this.cart?.items_subtotal_price || 0;
    return Format.money(subtotal, this.config.moneyFormat);
  }

  /**
   * Get formatted price for item
   * @param {CartItem} item - Cart item
   * @returns {string} Formatted price
   */
  getFormattedPrice(item) {
    if (!item) return Format.money(0, this.config.moneyFormat);
    return Format.money(item.price, this.config.moneyFormat);
  }

  /**
   * Get formatted line price for item
   * @param {CartItem} item - Cart item
   * @returns {string} Formatted line price
   */
  getFormattedLinePrice(item) {
    if (!item) return Format.money(0, this.config.moneyFormat);
    return Format.money(item.line_price, this.config.moneyFormat);
  }

  /**
   * Check if cart is empty
   * @returns {boolean} Is empty
   */
  isEmpty() {
    return this.getItemCount() === 0;
  }

  /**
   * Get cart items with memoization
   * @returns {CartItem[]} Cart items
   */
  getItems() {
    if (this._cache.items !== null && Date.now() - this._cache.lastUpdate < 1000) {
      return this._cache.items;
    }
    this._cache.items = this.cart?.items || [];
    return this._cache.items;
  }

  /**
   * Find item by variant ID
   * @param {number} variantId - Variant ID to find
   * @returns {CartItem|undefined} Found item
   */
  findItem(variantId) {
    return this.getItems().find(item => item.variant_id === variantId);
  }

  /**
   * Get enhanced performance metrics
   * @returns {Object} Performance metrics
   */
  getMetrics() {
    const now = Date.now();
    const last5Minutes = now - 5 * 60 * 1000;
    
    // Calculate average latencies
    const recentApiCalls = this.metrics.performance.apiLatency
      .filter(m => m.timestamp > last5Minutes);
    
    const recentDomUpdates = this.metrics.performance.domUpdates
      .filter(m => m.timestamp > last5Minutes);
    
    const recentEventHandling = this.metrics.performance.eventHandling
      .filter(m => m.timestamp > last5Minutes);
    
    const avgApiLatency = recentApiCalls.length ? 
      recentApiCalls.reduce((sum, m) => sum + m.duration, 0) / recentApiCalls.length : 0;
    
    const avgDomUpdate = recentDomUpdates.length ?
      recentDomUpdates.reduce((sum, m) => sum + m.duration, 0) / recentDomUpdates.length : 0;
    
    const avgEventHandling = recentEventHandling.length ?
      recentEventHandling.reduce((sum, m) => sum + m.duration, 0) / recentEventHandling.length : 0;
    
    return {
      apiCalls: this.metrics.apiCalls,
      errors: this.metrics.errors,
      events: Object.fromEntries(this.metrics.events),
      performance: {
        avgApiLatency,
        avgDomUpdate,
        avgEventHandling,
        recentApiCalls: recentApiCalls.length,
        recentDomUpdates: recentDomUpdates.length,
        recentEvents: recentEventHandling.length
      },
      lastUpdate: this.metrics.lastUpdate
    };
  }

  /**
   * Reset metrics and caches
   */
  resetMetrics() {
    this.metrics = {
      apiCalls: 0,
      errors: 0,
      lastUpdate: null,
      events: new Map(),
      performance: {
        apiLatency: [],
        domUpdates: [],
        eventHandling: []
      }
    };
    
    this._cache = {
      itemCount: null,
      total: null,
      items: null,
      lastUpdate: null
    };
    
    Debug.log('info', '[CartManager] Metrics and caches reset');
  }

  /**
   * Destroy cart manager
   */
  destroy() {
    // Remove all event listeners
    this.removeAllListeners();
    document.removeEventListener('submit', this.handleFormSubmit, true);
    document.removeEventListener('click', this.handleClick, true);
    document.removeEventListener('change', this.handleChange);
    
    // Clear any pending operations
    this.loadingOperations.clear();
    
    // Reset state
    this.cart = null;
    this.isLoading = false;
    
    Debug.log('info', 'CartManager destroyed');
  }

  /**
   * Check if we're currently on the cart page
   * @returns {boolean}
   */
  isOnCartPage() {
    return window.location.pathname === '/cart' || 
           document.querySelector('[data-cart-page]') !== null;
  }

  /**
   * Check if we're currently on a product page
   * @returns {boolean}
   */
  isOnProductPage() {
    return window.location.pathname.includes('/products/') || 
           document.querySelector('[data-product-form]') !== null;
  }

  getSections() {
    const sectionId = document.querySelector('[data-mini-cart]')?.closest('.shopify-section')?.getAttribute('id')?.replace('shopify-section-','');
    // Add more Id if needed
    return [sectionId]
  }

  /**
   * Get the current page context for cart operations
   * @returns {string} 'cart-page' | 'mini-cart' | 'product-page' | 'other'
   */
  getPageContext() {
    if (this.isOnCartPage()) {
      return 'cart-page';
    } else if (this.isOnProductPage()) {
      return 'product-page';
    } else if (document.querySelector('[data-mini-cart]') !== null) {
      return 'mini-cart';
    }
    return 'other';
  }
}
